import { oc } from "@orpc/contract";
import { z } from "zod";

export const DeferredIndexBuildStatusSchema = z.enum([
  "scheduled",
  "running",
  "completed",
  "failed",
  "cancelled",
  "interrupted",
]);
export type DeferredIndexBuildStatus = z.infer<
  typeof DeferredIndexBuildStatusSchema
>;

/** How a declared index compares with what the database holds. */
export const DeferredIndexStateSchema = z.enum([
  "missing",
  "valid",
  "invalid",
  "mismatched",
]);
export type DeferredIndexState = z.infer<typeof DeferredIndexStateSchema>;

export const DeferredIndexBuildSchema = z.object({
  id: z.string(),
  indexName: z.string(),
  status: DeferredIndexBuildStatusSchema,
  scheduledFor: z.string(),
  startedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  cancelRequestedAt: z.string().nullable(),
  beforeState: DeferredIndexStateSchema.nullable(),
  action: z.enum(["built", "rebuilt", "skipped"]).nullable(),
  elapsedMs: z.number().nullable(),
  error: z.string().nullable(),
  triggeredByUserId: z.string().nullable(),
  createdAt: z.string(),
});
export type DeferredIndexBuild = z.infer<typeof DeferredIndexBuildSchema>;

/** The live statement for a build in flight, from pg_stat_progress_create_index. */
export const ActiveDeferredIndexBuildSchema = z.object({
  startedAt: z.string().nullable(),
  phase: z.string().nullable(),
  blocksDone: z.number().nullable(),
  blocksTotal: z.number().nullable(),
  tuplesDone: z.number().nullable(),
  tuplesTotal: z.number().nullable(),
  waitingOn: z.string().nullable(),
});
export type ActiveDeferredIndexBuild = z.infer<
  typeof ActiveDeferredIndexBuildSchema
>;

export const DeferredIndexRowSchema = z.object({
  name: z.string(),
  table: z.string(),
  keys: z.array(z.string()),
  include: z.array(z.string()),
  predicate: z.string().nullable(),
  purpose: z.string(),
  state: DeferredIndexStateSchema,
  indexBytes: z.number().nullable(),
  tableBytes: z.number().nullable(),
  estimatedRows: z.number().nullable(),
  /** Share of table pages marked all-visible; index-only scans need this high. */
  allVisibleFraction: z.number().nullable(),
  lastVacuumAt: z.string().nullable(),
  active: ActiveDeferredIndexBuildSchema.nullable(),
  openBuild: DeferredIndexBuildSchema.nullable(),
  lastBuild: DeferredIndexBuildSchema.nullable(),
});
export type DeferredIndexRow = z.infer<typeof DeferredIndexRowSchema>;

export const deferredIndexesContracts = {
  listDeferredIndexes: oc
    .input(z.object({}))
    .output(z.object({ items: z.array(DeferredIndexRowSchema) }))
    .route({
      summary: "List deferred database indexes",
      description:
        "Admin only. Lists declared indexes built outside migrations with their state (missing, valid, invalid, mismatched), sizes, live build progress and last build.",
      method: "POST",
    }),

  scheduleIndexBuild: oc
    .input(
      z.object({
        indexName: z.string(),
        /** ISO timestamp. Omitted (or in the past) means as soon as the slot is free. */
        scheduledFor: z.iso.datetime({ offset: true }).optional(),
      }),
    )
    .output(DeferredIndexBuildSchema)
    .route({
      summary: "Schedule a database index build",
      description:
        "Admin only. Queues a build of a declared deferred index for a given time, or as soon as the build slot is free if no time is given.",
      method: "POST",
    }),

  cancelIndexBuild: oc
    .input(z.object({ runId: z.string() }))
    .output(
      z.object({
        /** `requested` for a running build: its process records the outcome. */
        result: z.enum(["cancelled", "requested"]),
      }),
    )
    .route({
      summary: "Cancel a database index build",
      description:
        "Admin only. Cancels a scheduled index build, or requests cancellation of a running one and stops its database statement.",
      method: "POST",
    }),

  listIndexBuilds: oc
    .input(
      z.object({
        indexName: z.string().optional(),
        page: z.number().int().positive().default(1),
        pageSize: z.number().int().positive().max(100).default(20),
      }),
    )
    .output(
      z.object({ items: z.array(DeferredIndexBuildSchema), total: z.number() }),
    )
    .route({
      summary: "List database index build history",
      description:
        "Admin only. Paged history of deferred index builds, optionally for one index, with status, action taken, timing and errors.",
      method: "POST",
    }),
};
